'use client';
import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setisLoggedIn, setRole } from '@/lib/slices/login-slice';

import SessionService from '@/services/storage/session-service';
import SessionValidationService from '@/services/auth/session-validation-service';

const PortalLayout = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): React.JSX.Element | undefined => {
  const router = useRouter();

  const store = useAppStore();
  const dispatch = useAppDispatch();
  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    //check login cookie here, redirect to sign-in otherwise
    const session = SessionService.getSessionCookie();
    if (session && session?.loggedIn) {
      dispatch(setisLoggedIn(true));
    } else {
      router.push('/');
    }
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) {
      // If logging out or not logged in, ensure any existing heartbeat is cleared
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    // Already running a heartbeat for this session
    if (heartbeatRef.current) return;

    // Callback for updating login redux state
    const callback = () => {
      dispatch(setisLoggedIn(false));
      dispatch(setRole(''));
    };

    // Start the heartbeat on portal enter
    const heartbeat = SessionValidationService.startHeartbeat(router, callback);
    heartbeatRef.current = heartbeat;

    // Cleanup heartbeat on unmount
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [loggedIn]);

  // Don't render anything while checking login status
  if (!loggedIn) {
    return <div>Loading...</div>;
  }

  return (
    <section id='portal-layout' className='portal-container'>
      {children}
    </section>
  );
};

export default PortalLayout;
